import type { CardMessage, PromptContext, ProposalStatus, SentPrompt } from '../types';
import { formatContext } from './prompts';
import { PROPOSE_CHANGES } from './tools';

/**
 * A provider-neutral view of the conversation. Each adapter maps these turns to its own wire format, or
 * replays an assistant turn's native record when it has one for the same provider.
 */
export type WireBlock =
  | { type: 'text'; text: string }
  | { type: 'tool_use'; id: string; name: string; input: Record<string, unknown> }
  | { type: 'tool_result'; toolUseId: string; content: string };

export interface WireTurn {
  role: 'user' | 'assistant';
  blocks: WireBlock[];
  /** The assistant message this turn came from, so an adapter can replay its native record instead. */
  source?: CardMessage;
}

export function describeStatus(status: ProposalStatus): string {
  switch (status) {
    case 'accepted':
      return 'The user accepted this proposal; the card now contains it.';
    case 'rejected':
      return 'The user rejected this proposal; the card is back to what it was before.';
    case 'superseded':
      return 'This proposal was replaced by a newer one before the user decided.';
    case 'pending':
      return 'The user has not decided on this proposal yet; the card shows it as a pending diff.';
  }
}

function textOf(message: CardMessage): string {
  return message.parts
    .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
    .map((p) => p.text)
    .join('\n')
    .trim();
}

type ProposalPart = Extract<CardMessage['parts'][number], { type: 'proposal' }>;

/** The input a proposal was made with, rebuilt from what Cue stored, in the tool schema's field order. */
export function proposalInput(p: ProposalPart['proposal']): Record<string, unknown> {
  return {
    ...(p.code !== undefined ? { language: p.language ?? '', code: p.code } : {}),
    ...(p.spec !== undefined ? { spec: p.spec } : {}),
    summary: p.summary,
  };
}

function proposalsOf(message: CardMessage | undefined): ProposalPart['proposal'][] {
  return message?.role === 'assistant'
    ? message.parts.filter((p): p is ProposalPart => p.type === 'proposal').map((p) => p.proposal)
    : [];
}

/**
 * What a new prompt sends, fixed at send time: the answers to the previous turn's proposals, as they stand
 * right now, and the prompt text with the card context. It is stored with the message and never rebuilt.
 */
export function buildSentPrompt(history: CardMessage[], text: string, context: PromptContext | undefined): SentPrompt {
  const previous = history[history.length - 1];
  return {
    toolResults: proposalsOf(previous).map((p) => ({ id: p.id, content: describeStatus(p.status) })),
    text: context ? `${text}\n\n${formatContext(context)}` : text,
  };
}

/** For prompts stored before `sent` existed: rebuild it the same way, from the message and the turn before it. */
function legacySent(message: CardMessage, previous: CardMessage | undefined): SentPrompt {
  const text = textOf(message);
  return {
    toolResults: proposalsOf(previous).map((p) => ({ id: p.id, content: describeStatus(p.status) })),
    text: message.context ? `${text}\n\n${formatContext(message.context)}` : text,
  };
}

export function toWireTurns(messages: CardMessage[]): WireTurn[] {
  const turns: WireTurn[] = [];
  const push = (turn: WireTurn) => {
    if (turn.blocks.length === 0) return;
    const prev = turns[turns.length - 1];
    if (prev && prev.role === 'user' && turn.role === 'user') prev.blocks.push(...turn.blocks);
    else turns.push(turn);
  };

  messages.forEach((m, i) => {
    if (m.role === 'user') {
      const sent = m.sent ?? legacySent(m, messages[i - 1]);
      const blocks: WireBlock[] = sent.toolResults.map((r) => ({ type: 'tool_result', toolUseId: r.id, content: r.content }));
      if (sent.text) blocks.push({ type: 'text', text: sent.text });
      push({ role: 'user', blocks });
      return;
    }
    const blocks: WireBlock[] = [];
    for (const part of m.parts) {
      if (part.type === 'text' && part.text.trim()) blocks.push({ type: 'text', text: part.text });
      if (part.type === 'proposal') blocks.push({ type: 'tool_use', id: part.proposal.id, name: PROPOSE_CHANGES, input: proposalInput(part.proposal) });
    }
    push({ role: 'assistant', blocks, source: m });
  });

  // A request must not leave a tool call unanswered. That only happens if the last message is an assistant turn.
  const last = messages[messages.length - 1];
  const open = proposalsOf(last);
  if (open.length > 0 && turns[turns.length - 1]?.role === 'assistant') {
    push({ role: 'user', blocks: open.map((p) => ({ type: 'tool_result', toolUseId: p.id, content: describeStatus(p.status) })) });
  }
  return turns;
}
